import {
  Box,
  Collapsible,
  Flex,
  IconButton,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HiCalendar,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from 'react-icons/hi2';
import dayjs from 'dayjs';
import { Button, SectionCard, ToggleButton } from '@themes/aegix';

type Period = 'hour' | 'day' | 'week';
const PERIODS: Period[] = ['hour', 'day', 'week'];

export interface DutyToggleSectionProps {
  isOnDuty: boolean;
  /** Called when toggling from off → on (back on duty immediately). */
  onToggleOn: () => void;
  /**
   * Called with the computed ISO timestamp when the user schedules going off
   * duty (period × count from now). The host adapter performs the mutation.
   */
  onScheduleOff: (untilISO: string) => void;
  /** Shown under the toggle when off duty with a known schedule end. */
  scheduledUntilLabel?: string;
  disabled?: boolean;
  title?: string;
  e2eTitle?: string;
}

/** Mirror of the navbar picker's math: hours are exact; days/weeks land at
 *  noon on the target day. */
function computeUntil(period: Period, count: number) {
  if (period === 'day')
    return dayjs().add(count, 'day').hour(12).minute(0).second(0);
  if (period === 'week')
    return dayjs().add(count, 'week').hour(12).minute(0).second(0);
  return dayjs().add(count, 'hour');
}

/**
 * Duty on/off toggle with an inline "go off duty until …" scheduler. Mounted
 * on the Profile's Assignments tab — in AIM via the shell's tab slot; in SMS
 * rendered first in AssignmentsSection.
 * Presentational: the period→ISO math lives here; the mutation
 * (and, in AIM, the redux sync) stays in each app's adapter.
 */
export function DutyToggleSection({
  isOnDuty,
  onToggleOn,
  onScheduleOff,
  scheduledUntilLabel,
  disabled = false,
  title,
  e2eTitle,
}: DutyToggleSectionProps) {
  const { t } = useTranslation();
  const [showScheduler, setShowScheduler] = useState(false);
  const [period, setPeriod] = useState<Period>('hour');
  const [count, setCount] = useState(1);

  const handleToggle = () => {
    if (isOnDuty) {
      // Going off duty is a scheduled action — open the picker.
      setShowScheduler(true);
    } else {
      setShowScheduler(false);
      onToggleOn();
    }
  };

  const handleSchedule = () => {
    onScheduleOff(computeUntil(period, count).toISOString());
    setShowScheduler(false);
    setCount(1);
  };

  const handleCancel = () => {
    setShowScheduler(false);
    setCount(1);
  };

  const formattedDate = computeUntil(period, count).format('MMM DD, YYYY, h:mm A');
  const untilLabel = t('scheduled_until_date', 'Off duty until {{date}}', { date: formattedDate });

  return (
    <SectionCard title={title ?? t('duty_status', 'Duty status')} e2eTitle={e2eTitle}>
      <Box data-e2e="profileDutyStatus" onClick={(e) => e.stopPropagation()}>
        <ToggleButton
          e2eTitle="profileDutyToggle"
          title={isOnDuty ? t('on_duty', 'On Duty') : t('off_duty', 'Off Duty')}
          checked={isOnDuty}
          disabled={disabled}
          onCheckedChange={() => handleToggle()}
        />

        {!isOnDuty && scheduledUntilLabel && (
          <Text fontSize="sm" color="text.secondary" mt={2}>
            {scheduledUntilLabel}
          </Text>
        )}

        <Collapsible.Root open={showScheduler}>
          <Collapsible.Content>
            <Box
              mt={4}
              p={4}
              borderRadius="lg"
              border="1px solid"
              borderColor="border.secondary"
              bg="surface.secondary"
              data-e2e="dutySchedule"
            >
              <Text fontSize="md" fontWeight={600} color="text.primary" mb={3}>
                {t('select_period', 'Select Period')}
              </Text>
              <SimpleGrid
                columns={3}
                textAlign="center"
                w="100%"
                border="1px solid"
                borderColor="border.secondary"
                borderRadius="lg"
                bg="surface.primary"
              >
                {PERIODS.map((p) => (
                  <Box
                    key={p}
                    py={2}
                    px={4}
                    borderRadius="lg"
                    cursor="pointer"
                    data-e2e={p}
                    background={
                      period === p ? 'surface.tertiary' : 'surface.primary'
                    }
                    onClick={() => {
                      setPeriod(p);
                      setCount(1);
                    }}
                  >
                    {t(p, p === 'hour' ? 'Hour' : p === 'day' ? 'Day' : 'Week')}
                  </Box>
                ))}
              </SimpleGrid>

              <Flex justifyContent="space-between" alignItems="center" mt={4}>
                <Text fontWeight="bold" color="text.primary">
                  {period === 'hour' && t('hours_from_now', '{{count}} hours from now', { count })}
                  {period === 'day' && t('days_from_now', '{{count}} days from now', { count })}
                  {period === 'week' && t('weeks_from_now', '{{count}} weeks from now', { count })}
                </Text>
                <Flex
                  border="1px solid"
                  borderColor="border.secondary"
                  borderRadius="lg"
                  overflow="hidden"
                  color="text.primary"
                >
                  <IconButton
                    aria-label={t('common.decrease', t('decrease', 'Decrease'))}
                    variant="ghost"
                    borderRadius={0}
                    disabled={count === 1}
                    onClick={() =>
                      setCount((prev) => (prev > 1 ? prev - 1 : 1))
                    }
                    _hover={{ bg: 'surface.tertiary' }}
                    data-e2e="prevButton"
                  >
                    <HiOutlineChevronLeft />
                  </IconButton>
                  <IconButton
                    aria-label={t('common.increase', t('increase', 'Increase'))}
                    variant="ghost"
                    borderRadius={0}
                    onClick={() => setCount((prev) => prev + 1)}
                    _hover={{ bg: 'surface.tertiary' }}
                    data-e2e="nextButton"
                  >
                    <HiOutlineChevronRight />
                  </IconButton>
                </Flex>
              </Flex>

              <Flex alignItems="center" gap={1} mt={4}>
                <Box fontSize="md" color="text.primary">
                  <HiCalendar />
                </Box>
                <Text fontSize="md" color="text.primary">
                  {untilLabel}
                </Text>
              </Flex>

              <Flex gap={3} mt={4}>
                <Button
                  visual="secondary"
                  onClick={handleCancel}
                  data-e2e="selectPeriodCancel"
                >
                  {t('common.cancel', t('cancel', 'Cancel'))}
                </Button>
                <Button
                  visual="primary"
                  onClick={handleSchedule}
                  data-e2e="selectPeriodSave"
                >
                  {t('common.save', t('save', 'Save'))}
                </Button>
              </Flex>
            </Box>
          </Collapsible.Content>
        </Collapsible.Root>
      </Box>
    </SectionCard>
  );
}

export default DutyToggleSection;
